import React, {
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from "react";
import { evaluate } from "mathjs";
import DisplayWindow from "./DisplayWindow";
import KeysWindow from "./KeysWindow";
import "./Calculator.scss";

const Calculator = forwardRef(function Calculator({ onEvaluate }, ref) {
  const [expression, setExpression] = useState("");
  const [displayEXP, setDisplayEXP] = useState("0");
  const [result, setResult] = useState("0");
  const [isInitial, setIsInitial] = useState(true); // initial state

  const sciFunc = {
    sin: "sin",
    cos: "cos",
    tan: "tan",
    ln: "log",
    log: "log10",
    π: "pi",
    e: "e",
    "^": "^",
    "√": "sqrt",
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      const { key } = event;

      if (key >= "0" && key <= "9") handleButton(key);
      else if (key === "Enter" || key === "=") handleButton("=");
      else if (key === "Backspace") handleButton("DEL");
      else if (key === "Escape") handleButton("AC");
      else if ("+-*/.()".includes(key)) handleButton(key);
      else if (key === "s") handleButton("sin");
      else if (key === "c") handleButton("cos");
      else if (key === "t") handleButton("tan");
      else if (key === "l") handleButton("ln");
      else if (key === "L") handleButton("log");
      else if (key === "p") handleButton("π");
      else if (key === "e") handleButton("e");
      else if (key === "^") handleButton("^");
      else if (key === "r") handleButton("√");
      else if (key === "!") handleButton("!");
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression, displayEXP]);

  // --- Trig in degrees ---
  function toDegreeExpr(expr) {
    const fns = ["sin", "cos", "tan"];
    let i = 0;
    let out = "";
    while (i < expr.length) {
      const fn = fns.find((name) => expr.startsWith(name + "(", i));
      if (!fn) {
        out += expr[i++];
        continue;
      }
      const startArgs = i + fn.length + 1;
      let j = startArgs,
        depth = 1;
      while (j < expr.length && depth > 0) {
        const ch = expr[j++];
        if (ch === "(") depth++;
        else if (ch === ")") depth--;
      }
      const inner = expr.slice(startArgs, j - 1);
      out += `${fn}((` + inner + `)*(pi/180))`;
      i = j;
    }
    return out;
  }

  // implicit multiply like: 2(3+4), 2sin(30), 2π, etc.
  function appendWithImplicitMultiply(prev, token) {
    if (!prev) return token;
    const last = prev.slice(-1);
    const startsWithFuncOrParen =
      token.startsWith("sin") ||
      token.startsWith("cos") ||
      token.startsWith("tan") ||
      token.startsWith("log") ||
      token.startsWith("sqrt") ||
      token.startsWith("pi") ||
      token.startsWith("e") ||
      token === "(";

    const lastIsNumberOrPiOrEorClose =
      /[0-9)]/.test(last) || prev.endsWith("pi") || prev.endsWith("e");

    if (startsWithFuncOrParen && lastIsNumberOrPiOrEorClose) {
      return prev + "*" + token;
    }
    return prev + token;
  }

  function calcResult(trigger = "=") {
    if (expression.length === 0) {
      setResult("An Error Occured!");
      setIsInitial(false);
      return;
    }

    try {
      const exprInDeg = toDegreeExpr(expression);
      let compute = evaluate(exprInDeg);
      compute = parseFloat(Number(compute).toFixed(4));
      setResult(compute);
      setIsInitial(false);

      // push to history (display vs engine kept separately)
      onEvaluate?.({
        id: Date.now(),
        exp: displayEXP,
        engine: expression,
        result: compute,
      });
    } catch {
      setResult("Error");
      setIsInitial(false);
    }
  }

  function handleButton(value) {
    if (value === "AC") {
      setExpression("");
      setDisplayEXP("0");
      setResult("0");
      setIsInitial(true);
      return;
    }

    if (value === "Ans" || value === "=") {
      calcResult(value);
      return;
    }

    if (value === "DEL") {
      if (isInitial) {
        setDisplayEXP("0");
        setExpression("");
        setResult("0");
      } else {
        const newDisplayEXP = displayEXP.slice(0, -1);
        const newExpression = expression.slice(0, -1);
        if (newDisplayEXP === "") {
          setDisplayEXP("0");
          setExpression("");
          setResult("0");
          setIsInitial(true);
        } else {
          setDisplayEXP(newDisplayEXP);
          setExpression(newExpression);
        }
      }
      return;
    }

    // scientific functions
    if (Object.prototype.hasOwnProperty.call(sciFunc, value)) {
      const token = sciFunc[value];
      if (isInitial) {
        setDisplayEXP(value);
        setExpression(token);
        setResult("0");
        setIsInitial(false);
      } else {
        setDisplayEXP(displayEXP + value);
        setExpression(appendWithImplicitMultiply(expression, token));
      }
      return;
    }

    // factorial (replace last number with computed factorial)
    if (value === "!") {
      if (isInitial) return;
      const lastNum = extractLastNum(expression);
      if (lastNum != null) {
        const num = parseFloat(lastNum);
        setDisplayEXP(displayEXP + value);
        setExpression(
          expression.replace(new RegExp(`${lastNum}$`), factorial(num))
        );
      }
      return;
    }

    // default input
    if (isInitial) {
      setExpression(value);
      setDisplayEXP(value);
      setResult("0");
      setIsInitial(false);
    } else {
      setExpression(appendWithImplicitMultiply(expression, value));
      setDisplayEXP(displayEXP + value);
    }
  }

  function factorial(n) {
    let r = 1;
    for (let i = 1; i <= n; i++) r *= i;
    return r;
  }

  function extractLastNum(exp) {
    const m = exp.match(/\d+(\.\d+)?$/);
    return m ? m[0] : null;
  }

  // Allow parent to load an old expression (replay from history)
  useImperativeHandle(ref, () => ({
    load: ({ display, engine }) => {
      setDisplayEXP(display || "0");
      setExpression(engine || "");
      setResult("0");
      setIsInitial(!engine);
    },
  }));

  return (
    <div className="calculator calc--dstl">
      <div className="calc-topbar">
        <span className="calc-brand">DSTL</span>
        <div className="calc-solar">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>

      <DisplayWindow expression={displayEXP} result={result} />
      <KeysWindow handleButton={handleButton} />
    </div>
  );
});

export default Calculator;
